import React from 'react';
import { useSelector } from 'react-redux';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { FormProvider } from 'react-hook-form';

import { getAccountDetails } from '../../../../api';
import { Text } from '@cowbell/ui';

import { getCowbellTranslations } from '../../../../i18n/translations';
import { FormLanguageProvider } from '../../../../i18n/forms/FormLanguageProvider';
import { determineIfAllowedScopes } from '../../../../utils/next/auth.utils';
import Breadcrumbs from '../../../breadcrumbs/Breadcrumbs';
import { Show } from '../../../Show';

import { OrgAttestation } from './OrgAttestation';
import { OrgBusinessAddressPanel } from './OrgBusinessAddressPanel';
import { OrgDetailsPanel } from './OrgDetailsPanel';
import { OrgMailingAddressPanel } from './OrgMailingAddressPanel';
import { OrgSaveBar } from './OrgSaveBar';
import {
  ORG_SECTIONS,
  OrgSectionNav,
  type OrgSectionId,
} from './OrgSectionNav';
import { useOrgAddressForm } from './useOrgAddressForm';
import { useOrgDetailsForm } from './useOrgDetailsForm';
import classes from './orgProfile.module.css';

interface OrgProfileProps {
  readonly accountId: string;
}

/**
 * The policyholder's Organization Profile.
 *
 * <p>Three panels down the page with a section rail beside them and one save
 * bar at the foot, per the supplied design. The two forms underneath stay
 * separate -- Organization Details and the address pair each keep their own
 * schema and their own `updateOrgProfile` call -- and the bar submits whichever
 * of them is dirty.
 *
 * <p>Nothing on this page is gated by `submissions_view`. The accordion it
 * replaces rendered every field read-only whenever that flag was on, and told
 * the policyholder to contact their agent; a policyholder can now edit their
 * own details whatever the state of their policy (FIR-21111).
 */
function OrgProfile({ accountId }: OrgProfileProps) {
  const scopes = useSelector((state: any) => state.scopes);
  const [loading, setLoading] = React.useState(true);
  const [orgData, setOrgData] = React.useState<any>({});
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    setOrgData({});
    setFailed(false);
    getAccountDetails(accountId)
      .then(({ data }: any) => setOrgData(data))
      .catch((error: unknown) => {
        console.error(error);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, [accountId]);

  const { t } = getCowbellTranslations();

  const canVerify = determineIfAllowedScopes(scopes, [
    'account_profile:verify',
  ]);

  if (loading) {
    return <div>Loading...</div>;
  }

  // Without this the page renders a blank but fully editable form when the
  // account cannot be read -- every field empty, every required-field count
  // maxed out, and a save that can only fail. It reads as a broken profile
  // rather than as a failed request.
  if (failed || !orgData?.id) {
    return (
      <Text ta="center" size="lg" py="xl">
        {`We could not load this ${organizationLabelFor(
          t
        )} profile. Please refresh, or contact Cowbell support if it keeps happening.`}
      </Text>
    );
  }

  return (
    <OrgProfileForms
      accountId={accountId}
      orgData={orgData}
      canVerify={canVerify}
      organizationLabel={t('organization')}
    />
  );
}

interface OrgProfileFormsProps {
  readonly accountId: string;
  readonly orgData: any;
  readonly canVerify: boolean;
  readonly organizationLabel: string;
}

/**
 * Mounted only once the account has loaded, so both forms take their default
 * values from real data on their first render rather than resetting under the
 * user.
 */
function OrgProfileForms({
  accountId,
  orgData,
  canVerify,
  organizationLabel,
}: OrgProfileFormsProps) {
  const detailsForm = useOrgDetailsForm({ accountId, orgData });
  const addressForm = useOrgAddressForm({ accountId, orgData });

  const [activeSection, setActiveSection] =
    React.useState<OrgSectionId>('details');
  const [saving, setSaving] = React.useState(false);
  const [justSaved, setJustSaved] = React.useState(false);

  useActiveSection(setActiveSection);

  const detailsDirty = detailsForm.isDirty;
  const addressDirty = addressForm.isDirty;
  const dirty = detailsDirty || addressDirty;

  const missingBySection: Record<OrgSectionId, number> = {
    details: detailsForm.missingCount,
    address: addressForm.businessMissingCount,
    mailing: addressForm.mailingMissingCount,
  };

  const totalMissing =
    missingBySection.details +
    missingBySection.address +
    missingBySection.mailing;

  const handleSelectSection = (id: OrgSectionId) => {
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
    setActiveSection(id);
  };

  /**
   * Submits whichever forms are dirty.
   *
   * <p>Each `submit` resolves with whether it actually wrote, because
   * `handleSubmit` resolves whether or not its handler ran. Reporting "Saved"
   * off the mere fact that the promises settled showed the flag for a save
   * that had failed or that the resolver had blocked. `saving` is cleared in a
   * `finally` so nothing can leave the bar stuck on "Saving".
   */
  const handleSave = async () => {
    const submits = [];
    if (detailsDirty) {
      submits.push(detailsForm.submit());
    }
    if (addressDirty) {
      submits.push(addressForm.submit());
    }

    if (submits.length === 0) {
      return;
    }

    setSaving(true);
    try {
      const results = await Promise.all(submits);
      setJustSaved(results.every(Boolean));
    } catch (error) {
      // Each form reports its own failures, so reaching here means something
      // unexpected threw. Swallowed rather than left to become an unhandled
      // rejection in a click handler; the form stays dirty, so the bar stays
      // up and the edit can be retried.
      console.error(error);
      setJustSaved(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    detailsForm.reset();
    addressForm.reset();
  };

  React.useEffect(() => {
    if (!justSaved) {
      return undefined;
    }
    const timer = window.setTimeout(() => setJustSaved(false), 2400);
    return () => window.clearTimeout(timer);
  }, [justSaved]);

  return (
    <div className={classes.page}>
      <header className={classes.header}>
        <Breadcrumbs title={orgData.name}>
          <p>Manage</p>
          <p>{`${organizationLabel} profile`}</p>
        </Breadcrumbs>
        <Show when={justSaved}>
          <span className={classes.savedFlag}>
            <CheckCircleOutlineIcon fontSize="small" aria-hidden="true" />
            Saved
          </span>
        </Show>
      </header>

      <div className={classes.body}>
        <aside className={classes.rail}>
          <OrgSectionNav
            activeId={activeSection}
            missingBySection={missingBySection}
            onSelect={handleSelectSection}
          />
          <p className={classes.railSummary}>{summaryCopy(totalMissing)}</p>
        </aside>

        <div className={classes.panels}>
          <FormProvider {...detailsForm.methods}>
            <OrgDetailsPanel form={detailsForm} canVerify={canVerify} />
          </FormProvider>

          <FormLanguageProvider language={addressForm.language as never}>
            <FormProvider {...addressForm.methods}>
              <OrgBusinessAddressPanel form={addressForm} />
              <OrgMailingAddressPanel form={addressForm} />

              <Show when={canVerify}>
                <OrgAttestation disabled={!dirty} />
              </Show>
            </FormProvider>
          </FormLanguageProvider>
        </div>
      </div>

      <OrgSaveBar
        visible={dirty}
        saving={saving}
        missingCount={totalMissing}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />
    </div>
  );
}

/** The tenant's word for "organization", lowercased for mid-sentence use. */
const organizationLabelFor = (t: (key: string) => string): string =>
  t('organization').toLowerCase();

const summaryCopy = (totalMissing: number): string => {
  if (totalMissing === 0) {
    return 'All required information is on file.';
  }

  if (totalMissing === 1) {
    return '1 required field is still empty.';
  }

  return `${totalMissing} required fields are still empty.`;
};

/**
 * Keeps the rail's highlight on whichever panel the reader is looking at.
 *
 * <p>jsdom has no IntersectionObserver, so under test the rail simply stays on
 * its default section.
 */
function useActiveSection(onChange: (id: OrgSectionId) => void) {
  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          )[0];

        if (visible) {
          onChange(visible.target.id as OrgSectionId);
        }
      },
      { rootMargin: '-120px 0px -55% 0px', threshold: 0 }
    );

    ORG_SECTIONS.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default OrgProfile;
